import { Badge, Box, Button, Flex, HStack, IconButton, Menu, MenuButton, MenuItem, MenuList, Progress, SimpleGrid, Stack, Stat, StatHelpText, StatLabel, StatNumber, Text } from '@chakra-ui/react';
import { DataTable, DeleteDialog, EmptyTable, LoadingTable } from '../../components';
import { FiChevronDown, FiUsers } from 'react-icons/fi';
import React, { useEffect, useState } from 'react';
import { deleteUser, fetchUsers, updateUserRole } from '../../store/actions';
import { useAppDispatch, useAppSelector } from '../../hooks';

import { AppDispatch } from '../../store';
import CountUp from 'react-countup';
import { Link as DefaultLink } from 'react-router-dom';
import { GrRefresh } from 'react-icons/gr';
import { HiOutlineArrowTrendingUp } from 'react-icons/hi2';
import { ToastContainer } from 'react-toastify';
import { companyUserType } from '../../types';
import { createColumnHelper } from '@tanstack/react-table';
import { filterByRole } from '../../utils';

export const ViewUsers = () => {

    const [isOpen, setDeleteDialog] = useState<boolean>(false);
    const [roles] = useState<Array<string>>(['driver', 'mechanic', 'admin']);

    const { users, is_loading_users } = useAppSelector(state => state.usersReducer);

    const dispatch: AppDispatch = useAppDispatch();

    const data: companyUserType[] = users;

    const columnHelper = createColumnHelper<companyUserType | any>();

    const columns = [
        columnHelper.accessor('user_id', {
            cell: (info) => info.getValue() ?? '-',
            header: 'User ID'
        }),
        columnHelper.accessor('first_name', {
            cell: (info) => info.getValue() ?? '-',
            header: 'First Name'
        }),
        columnHelper.accessor('last_name', {
            cell: (info) => info.getValue() ?? '-',
            header: 'Last Name'
        }),
        columnHelper.accessor('email', {
            cell: (info) => info.getValue() ?? '-',
            header: 'Email'
        }),
        columnHelper.accessor('phone', {
            cell: (info) => info.getValue() ?? '-',
            header: 'Phone',
        }),
        columnHelper.accessor('gender', {
            cell: (info) => info.getValue() ?? '-',
            header: 'Gender',
        }),
        columnHelper.accessor('role', {
            cell: ({ row }) => {
                const user: companyUserType = row?.original;
                return (
                    <>
                        <Menu>
                            <MenuButton
                                as={IconButton}
                                aria-label='Options'
                                icon={
                                    <HStack bg={user?.role === 'driver' ? '#c6f4d2' : user?.role === 'mechanic' ? '#fefcbf' : '#edf2f7'}>
                                        <Badge py={1} colorScheme={user?.role === 'driver' ? 'green' : user?.role === 'mechanic' ? 'yellow' : 'gray'} pl={2} borderRadius={3}>{user?.role}</Badge>
                                        <FiChevronDown style={{ paddingRight: 3 }} />
                                    </HStack>
                                }
                                variant='outline'
                                style={{ height: '0px' }}
                            />
                            <MenuList>
                                {
                                    roles?.map((role, index) => (
                                        <MenuItem key={index} textTransform={'capitalize'} onClick={() => handleUpdateRole(`${user?._id}`, role, user)}>
                                            {role}
                                        </MenuItem>
                                    ))
                                }
                            </MenuList>
                        </Menu>
                    </>
                );
            },
            header: 'Role',
        }),
        columnHelper.accessor('', {
            cell: ({ row }) => {
                const user: companyUserType = row?.original;
                return (
                    <>
                        <HStack>
                            <Button colorScheme={'teal'} variant={'outline'} size='xs' as={DefaultLink} to={`/users/${user?.user_id}/edit`}>
                                Edit
                            </Button>
                            <Button variant={'outline'} colorScheme={'red'} size='xs' onClick={() => setDeleteDialog(!isOpen)}>
                                {/* {on_vehicle_delete ? '...' : 'Delete'} */} Delete
                            </Button>
                        </HStack>
                        <DeleteDialog
                            id={`${user?._id}`}
                            item='Vehicle'
                            isOpen={isOpen}
                            onDelete={() => handleDeleteUser(user?._id)}
                            onClose={() => setDeleteDialog(!isOpen)}
                        />
                    </>
                );
            },
            header: 'Actions',
        })
    ];

    const handleRefresh = () => {
        dispatch(fetchUsers());
    };

    // const handleAssignDriver = (id: string, driver_id: string, driver_name: string, values: vehicelType) => {
    //     const newValues: vehicelType = { ...values, driver_id, driver_name };
    //     dispatch(assignDriver(id, newValues));
    // };

    const handleUpdateRole = (id: string, role: string, values: companyUserType) => {
        const { user_id, email, first_name, last_name, phone, gender, company_id } = values;
        const newValues: any = { user_id, email, first_name, last_name, phone, gender, role, company_id };
        dispatch(updateUserRole(id, newValues));
    };

    const handleDeleteUser = (id: string) => {
        dispatch(deleteUser(id));
        setDeleteDialog(!isOpen);
    };

    useEffect(() => {
        dispatch(fetchUsers());
    }, [is_loading_users]);

    return (
        <>
            <Flex
                minH={'-webkit-fill-available'}
                align={'center'}
                justify={'center'}
                pt={2}
                pb={10}
            >
                <Stack>
                    <Stack mb={3}>
                        <SimpleGrid columns={[1, null, 3]} spacing={6}>
                            <Stat bgColor={'white'} px={4} py={3} borderRadius={6}>
                                <StatLabel textTransform={'uppercase'} color={'gray.500'} fontSize={'xs'} fontWeight={'medium'}>All users</StatLabel>
                                <HStack>
                                    <CountUp start={0} end={users.length || 0} duration={2}>
                                        {
                                            ({ countUpRef }) => (
                                                <StatNumber fontSize={'4xl'}><span ref={countUpRef}>{users.length || 0}</span></StatNumber>
                                            )
                                        }
                                    </CountUp>
                                    <HiOutlineArrowTrendingUp color='#4caf50' size={24} fontWeight={700} />
                                </HStack>
                                <StatHelpText color={'gray.500'} fontSize={'xs'}>All company users</StatHelpText>
                            </Stat>
                            <Box w='100%'>
                                <HStack bg={'#FFFFFF'} p={4} borderRadius={6}>
                                    <Stack p={6} bg='#f5f5f5' borderRadius={'50%'} mr={2}>
                                        <FiUsers style={{ width: '40px', height: '40px' }} />
                                    </Stack>
                                    <Stat>
                                        <Badge minW={'80px'} bg={'#ffecb3'} color={'#212121'} px={2} borderRadius={3}>mechanic</Badge>
                                        <StatNumber my={1}>{filterByRole(users, 'mechanic').length}<Text fontSize={'18px'} color={'#bdbdbd'} display={'inline'}>/{users?.length}</Text></StatNumber>
                                        <Progress borderRadius={2} colorScheme='yellow' size='sm' value={(filterByRole(users, 'mechanic').length / users.length) * 100} />
                                    </Stat>
                                </HStack>
                            </Box>
                            <Box w='100%'>
                                <HStack bg={'#FFFFFF'} p={4} borderRadius={6}>
                                    <Stack p={6} bg='#f5f5f5' borderRadius={'50%'} mr={2}>
                                        <FiUsers style={{ width: '40px', height: '40px' }} />
                                    </Stack>
                                    <Stat>
                                        <Badge minW={'80px'} colorScheme={'green'} px={2} borderRadius={3}>drivers</Badge>
                                        <StatNumber>{filterByRole(users, 'driver').length}<Text fontSize={'18px'} color={'#bdbdbd'} display={'inline'}>/{users?.length}</Text></StatNumber>
                                        <Progress borderRadius={2} colorScheme='green' size='sm' value={(filterByRole(users, 'driver').length / users.length) * 100} />
                                    </Stat>
                                </HStack>
                            </Box>
                        </SimpleGrid>
                    </Stack>
                    <Stack bg={'#FFFFFF'} p={4} borderRadius={6}>
                        <HStack justifyContent={'space-between'} mb={4} px={2}>
                            <Text fontSize={20} fontWeight={'medium'}>Users Table</Text>
                            <HStack>
                                <IconButton aria-label='Refresh button' size={'sm'} icon={<GrRefresh className={false ? 'loading-refresh-icon' : ''} />} onClick={handleRefresh} />
                                <Button bg={'#ffca28'} size='sm' as={DefaultLink} to='/users/add'>
                                    Add User
                                </Button>
                            </HStack>
                        </HStack>
                        <SimpleGrid columns={[1, null, 1]} spacing='40px'>
                            <Box w='100%'>
                                <DataTable columns={columns} data={data} variant='striped' />
                                {is_loading_users && !users.length && <LoadingTable />}
                                {!users.length && !is_loading_users && <EmptyTable title='Oops!' description='Your company does not have users.' />}

                            </Box>
                        </SimpleGrid>
                    </Stack>
                    <ToastContainer />
                </Stack>
            </Flex>
        </>
    );
};